/**
 * useTradeNotifications Hook
 *
 * React hook for managing trade notifications.
 * Handles real-time trade events and provides methods to accept/decline/counter.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { PresenceSocket, getSharedPresenceSocket, releaseSharedPresenceSocket } from '../services/presence-socket';
import type { TradeNotification } from '@drawn-of-war/shared';

/**
 * Hook return type
 */
export interface UseTradeNotificationsResult {
  /** Array of active trades (pending/negotiating) */
  trades: TradeNotification[];
  /** Accept a trade */
  acceptTrade: (tradeId: string) => Promise<void>;
  /** Decline a trade */
  declineTrade: (tradeId: string) => Promise<void>;
  /** Counter a trade with a different creature */
  counterTrade: (tradeId: string, creatureId: string) => Promise<void>;
  /** Create a new trade offer */
  createTrade: (targetUserId: string, targetCreatureId: string, offeredCreatureId: string) => Promise<string>;
  /** Loading state for trade actions */
  isLoading: boolean;
  /** Error message if any action failed */
  error: string | null;
  /** Clear error */
  clearError: () => void;
  /** Refetch active trades */
  refetch: () => Promise<void>;
}

/**
 * useTradeNotifications Hook
 *
 * Manages trade notifications including:
 * - Fetching active trades on mount
 * - Real-time updates via Socket.IO
 * - Accept/decline/counter functionality
 * - Auto-remove expired trades
 *
 * @returns Object with trades array and action methods
 */
export function useTradeNotifications(): UseTradeNotificationsResult {
  const [trades, setTrades] = useState<TradeNotification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user, getIdToken } = useAuth();
  const socketRef = useRef<PresenceSocket | null>(null);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Fetch active trades from API
  const fetchActiveTrades = useCallback(async () => {
    // Don't fetch if not authenticated
    if (!user) return;

    try {
      const token = await getIdToken();
      if (!token) return; // No token available yet

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3002';

      const response = await fetch(`${apiUrl}/api/trades/active`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTrades(data.trades);
      }
    } catch (err) {
      console.error('[useTradeNotifications] Failed to fetch active trades:', err);
    }
  }, [user, getIdToken]);

  // Create a new trade offer
  const createTrade = useCallback(async (
    targetUserId: string,
    targetCreatureId: string,
    offeredCreatureId: string
  ): Promise<string> => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getIdToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3002';

      const response = await fetch(`${apiUrl}/api/trades`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ targetUserId, targetCreatureId, offeredCreatureId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create trade');
      }

      // Refetch trades to include the new one
      await fetchActiveTrades();

      return data.tradeId;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create trade';
      setError(errorMessage);
      console.error('[useTradeNotifications] Create trade failed:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [getIdToken, fetchActiveTrades]);

  // Accept a trade
  const acceptTrade = useCallback(async (tradeId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getIdToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3002';

      const response = await fetch(`${apiUrl}/api/trades/${tradeId}/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to accept trade');
      }

      // Remove trade from list
      setTrades(prev => prev.filter(t => t.id !== tradeId));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to accept trade';
      setError(errorMessage);
      console.error('[useTradeNotifications] Accept failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [getIdToken]);

  // Decline a trade
  const declineTrade = useCallback(async (tradeId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getIdToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3002';

      const response = await fetch(`${apiUrl}/api/trades/${tradeId}/decline`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to decline trade');
      }

      // Remove trade from list
      setTrades(prev => prev.filter(t => t.id !== tradeId));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to decline trade';
      setError(errorMessage);
      console.error('[useTradeNotifications] Decline failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [getIdToken]);

  // Counter a trade with a different creature
  const counterTrade = useCallback(async (tradeId: string, creatureId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getIdToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3002';

      const response = await fetch(`${apiUrl}/api/trades/${tradeId}/counter`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ creatureId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to counter trade');
      }

      // Refetch to get updated trade state
      await fetchActiveTrades();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to counter trade';
      setError(errorMessage);
      console.error('[useTradeNotifications] Counter failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [getIdToken, fetchActiveTrades]);

  // Setup socket listeners and fetch initial trades
  useEffect(() => {
    // Don't setup until user is authenticated
    if (!user) return;

    // Fetch active trades on mount
    fetchActiveTrades();

    // Get shared socket connection for real-time updates
    const socket = getSharedPresenceSocket();
    socketRef.current = socket;

    // New trade offer received
    socket.onTradeOfferReceived((trade: TradeNotification) => {
      console.log('[useTradeNotifications] Trade offer received:', trade);
      setTrades(prev => {
        // Avoid duplicates
        const exists = prev.some(t => t.id === trade.id);
        if (exists) return prev;
        return [...prev, trade];
      });
    });

    // Counter-offer received
    socket.onTradeCounter((trade: TradeNotification) => {
      console.log('[useTradeNotifications] Trade counter received:', trade);
      setTrades(prev => {
        // Update existing trade or add if new
        const exists = prev.some(t => t.id === trade.id);
        if (exists) {
          return prev.map(t => t.id === trade.id ? trade : t);
        }
        return [...prev, trade];
      });
    });

    // Trade completed
    socket.onTradeCompleted((data) => {
      console.log('[useTradeNotifications] Trade completed:', data);
      setTrades(prev => prev.filter(t => t.id !== data.tradeId));
    });

    // Trade declined
    socket.onTradeDeclined((data) => {
      console.log('[useTradeNotifications] Trade declined:', data);
      setTrades(prev => prev.filter(t => t.id !== data.tradeId));
    });

    // Trade expired
    socket.onTradeExpired((data) => {
      console.log('[useTradeNotifications] Trade expired:', data);
      setTrades(prev => prev.filter(t => t.id !== data.tradeId));
    });

    // Setup expiration checker (runs every 30 seconds)
    const expirationInterval = setInterval(() => {
      const now = new Date();
      setTrades(prev =>
        prev.filter(trade => new Date(trade.expiresAt) > now)
      );
    }, 30000);

    // Cleanup on unmount — remove listeners, release shared ref
    return () => {
      if (socketRef.current) {
        socketRef.current.offTradeListeners();
        socketRef.current = null;
      }
      releaseSharedPresenceSocket();
      clearInterval(expirationInterval);
    };
  }, [user, fetchActiveTrades]);

  return {
    trades,
    acceptTrade,
    declineTrade,
    counterTrade,
    createTrade,
    isLoading,
    error,
    clearError,
    refetch: fetchActiveTrades
  };
}

/**
 * useTradeCountdown Hook
 *
 * Returns a formatted countdown string for trade expiration.
 * Displays hours:minutes:seconds for trades.
 */
export function useTradeCountdown(expiresAt: string): string {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const expiry = new Date(expiresAt);
      const diff = expiry.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeLeft('Expired');
        return;
      }

      const hours = Math.floor(diff / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);

      if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m`);
      } else {
        setTimeLeft(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      }
    };

    // Update immediately
    updateCountdown();

    // Update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [expiresAt]);

  return timeLeft;
}
